import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Plus,
  TrendingUp,
  DollarSign,
  Home,
  Calculator,
  FileText,
  ArrowUpRight,
  CheckCircle2,
  Loader2,
  Trash2,
  Gauge,
  ArrowRight,
  Crown,
  Gift,
  Building2,
  Clock,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Progress } from '../components/ui/progress';
import { UpgradeBanner } from '../components/UpgradePrompts';
import OnboardingGuide from '../components/OnboardingGuide';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const TrialCountdownBanner = ({ subscription }) => {
  const navigate = useNavigate();
  if (!subscription) return null;
  const { status, plan, planName, expiresAt, monthlyPrice, billingPeriod } = subscription;
  if (status !== 'trialing' || !expiresAt) return null;

  const now = new Date();
  const expires = new Date(expiresAt);
  const daysLeft = Math.max(0, Math.ceil((expires - now) / (1000 * 60 * 60 * 24)));
  const bp = billingPeriod || 6;
  const totalCharge = monthlyPrice ? (monthlyPrice * bp).toFixed(2) : null;
  const periodLabel = { 1: '1 month', 6: '6 months', 12: '12 months' }[bp] || `${bp} months`;
  const progress = Math.max(0, Math.min(100, ((7 - daysLeft) / 7) * 100));
  const isUrgent = daysLeft <= 2;

  return (
    <div
      className={`relative overflow-hidden rounded-xl p-[1px] mb-6 ${
        isUrgent
          ? 'bg-gradient-to-r from-orange-500 to-red-500'
          : 'bg-gradient-to-r from-cyan-500 to-purple-500'
      }`}
      data-testid="trial-countdown-banner"
    >
      <div className={`rounded-[11px] px-6 py-4 ${
        isUrgent ? 'bg-orange-950/95' : 'bg-gray-900/95'
      }`}>
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-4 min-w-0">
            <div className={`w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0 ${
              isUrgent ? 'bg-orange-500/20' : 'bg-cyan-500/20'
            }`}>
              <Clock className={`w-5 h-5 ${isUrgent ? 'text-orange-400' : 'text-cyan-400'}`} />
            </div>
            <div className="min-w-0">
              <p className="text-white font-bold text-sm sm:text-base">
                {daysLeft === 0 ? 'Your trial ends today!' : `${daysLeft} day${daysLeft !== 1 ? 's' : ''} left on your ${planName || plan} trial`}
              </p>
              <p className="text-gray-300 text-xs sm:text-sm">
                {totalCharge
                  ? `You'll be charged $${totalCharge} for ${periodLabel} ($${monthlyPrice}/mo) when it ends`
                  : 'Your subscription will activate when the trial ends'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3 flex-shrink-0">
            <Button
              size="sm"
              variant="outline"
              className="border-gray-600 text-gray-300 hover:bg-gray-800 text-xs"
              onClick={() => navigate('/pricing')}
              data-testid="trial-manage-plan-btn"
            >
              Change Plan
            </Button>
          </div>
        </div>
        <div className="mt-3">
          <div className="flex justify-between text-[10px] text-gray-400 mb-1">
            <span>Trial started</span>
            <span>{expires.toLocaleDateString()}</span>
          </div>
          <div className="w-full bg-gray-700/50 rounded-full h-1.5">
            <div
              className={`h-1.5 rounded-full transition-all ${
                isUrgent ? 'bg-gradient-to-r from-orange-500 to-red-500' : 'bg-gradient-to-r from-cyan-500 to-purple-500'
              }`}
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const DashboardPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { isAuthenticated, token, user, loading: authLoading } = useAuth();
  
  const [deals, setDeals] = useState([]);
  const [stats, setStats] = useState(null);
  const [usage, setUsage] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);
  const [discordCount, setDiscordCount] = useState(null);

  useEffect(() => {
    if (!authLoading) {
      if (!isAuthenticated) {
        navigate('/login');
        return;
      }
      fetchDeals();
      fetchStats();
      fetchUsage();
      fetchSubscription();
    }
  }, [isAuthenticated, authLoading, navigate, token]);

  useEffect(() => {
    fetch('https://discord.com/api/v9/invites/aZ8Pp4ws?with_counts=true')
      .then(r => r.json())
      .then(d => setDiscordCount({
        members: d.approximate_member_count || 0,
        online: d.approximate_presence_count || 0,
      }))
      .catch(() => {});
  }, []);

  const fetchDeals = async () => {
    try {
      const response = await fetch(`${API_URL}/api/saved-deals/my-deals?limit=5`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setDeals(data);
      }
    } catch (error) {
      console.error('Error fetching deals:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await fetch(`${API_URL}/api/saved-deals/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setStats(await response.json());
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchUsage = async () => {
    try {
      const response = await fetch(`${API_URL}/api/subscriptions/usage`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setUsage(await response.json());
      }
    } catch (error) {
      console.error('Error fetching usage:', error);
    }
  };

  const fetchSubscription = async () => {
    try {
      const response = await fetch(`${API_URL}/api/subscriptions/current`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setSubscription(await response.json());
      }
    } catch (error) {
      console.error('Error fetching subscription:', error);
    }
  };

  const handleDeleteDeal = async (dealId, e) => {
    e.stopPropagation();
    if (!window.confirm(t('dashboard.confirmDeleteDeal'))) return;
    
    setDeletingId(dealId);
    try {
      const response = await fetch(`${API_URL}/api/saved-deals/${dealId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success(t('dashboard.dealDeleted'));
        fetchDeals();
        fetchStats();
      } else {
        toast.error(t('dashboard.failedToDelete'));
      }
    } catch (error) {
      toast.error(t('dashboard.failedToDelete'));
    } finally {
      setDeletingId(null);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value || 0);
  };

  const formatPercent = (value) => {
    return `${(value || 0).toFixed(1)}%`;
  };

  const statsData = [
    { label: t('dashboard.totalDeals'), value: stats?.totalDeals || 0, change: '+3', icon: Home, color: 'purple' },
    { label: t('dashboard.avgProfit'), value: formatCurrency(stats?.totalProfit || 0), change: '+18%', icon: TrendingUp, color: 'pink' },
    { label: t('dashboard.avgRoi'), value: formatPercent(stats?.avgRoi || 0), change: '+2.1%', icon: DollarSign, color: 'purple' },
    { label: t('dashboard.totalArv'), value: formatCurrency(stats?.totalArv || 0), change: '+7', icon: FileText, color: 'pink' }
  ];

  const getStatusBadge = (status) => {
    switch (status) {
      case 'analyzing':
        return <Badge className="bg-purple-100 text-purple-700 dark:bg-purple-900/50 dark:text-pink-400 border-0">{t('dashboard.analyzing')}</Badge>;
      case 'active':
        return <Badge className="bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-300 border-0">{t('dashboard.active')}</Badge>;
      case 'under_contract':
        return <Badge className="bg-yellow-100 text-yellow-700 dark:bg-yellow-900/50 dark:text-yellow-300 border-0">{t('dashboard.underContract')}</Badge>;
      case 'closed':
        return <Badge className="bg-gray-100 text-gray-700 dark:bg-gray-700/50 dark:text-gray-300 border-0">{t('dashboard.closed')}</Badge>;
      case 'archived':
        return <Badge className="bg-gray-100 text-gray-600 dark:bg-gray-700/50 dark:text-gray-400 border-0">{t('dashboard.archived')}</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getStrategyBadge = (strategy) => {
    const colors = {
      flip: 'bg-pink-100 text-pink-700 dark:bg-pink-900/50 dark:text-pink-300',
      brrrr: 'bg-purple-100 text-purple-700 dark:bg-purple-900/50 dark:text-pink-400',
      wholesale: 'bg-fuchsia-100 text-fuchsia-700 dark:bg-fuchsia-900/50 dark:text-fuchsia-300',
      rental: 'bg-violet-100 text-violet-700 dark:bg-violet-900/50 dark:text-violet-300'
    };
    return (
      <Badge className={`${colors[strategy] || 'bg-gray-100 text-gray-700 dark:bg-gray-700/50 dark:text-gray-300'} border-0`}>
        {strategy?.toUpperCase()}
      </Badge>
    );
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-pink-50 dark:from-gray-900 dark:via-gray-900 dark:to-purple-950 pt-20 flex items-center justify-center transition-colors">
        <Loader2 className="w-8 h-8 text-purple-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-pink-50 dark:from-gray-900 dark:via-gray-900 dark:to-purple-950 pt-20 transition-colors duration-300 overflow-x-hidden">
      <OnboardingGuide />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-1" data-testid="dashboard-title">{t('dashboard.title')}</h1>
            <p className="text-gray-600 dark:text-gray-400">{t('dashboard.subtitle')}</p>
          </div>
          <Link to="/analyzer">
            <Button className="bg-gradient-to-r from-purple-600 to-pink-500 hover:from-purple-700 hover:to-pink-600 text-white shadow-lg shadow-purple-500/25">
              <Plus className="w-4 h-4 mr-2" />
              {t('common.newAnalysis')}
            </Button>
          </Link>
        </div>

        {/* Upgrade Banner for Free Users */}
        <UpgradeBanner plan={usage?.plan} />

        {/* Trial Countdown Banner */}
        <TrialCountdownBanner subscription={subscription} />

        {/* Stats Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {statsData.map((stat, index) => (
            <Card key={index} className="border-purple-100 dark:border-purple-900/50 dark:bg-gray-800/50 hover:shadow-md transition-shadow hover:border-purple-200 dark:hover:border-purple-700">
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className={`w-12 h-12 ${stat.color === 'purple' ? 'bg-purple-100 dark:bg-purple-900/50' : 'bg-pink-100 dark:bg-pink-900/50'} rounded-xl flex items-center justify-center`}>
                    <stat.icon className={`w-6 h-6 ${stat.color === 'purple' ? 'text-purple-600 dark:text-pink-400' : 'text-pink-600 dark:text-pink-400'}`} />
                  </div>
                  <span className="text-sm text-green-600 dark:text-green-400 font-medium flex items-center gap-1">
                    <ArrowUpRight className="w-3 h-3" />
                    {stat.change}
                  </span>
                </div>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">{stat.label}</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{stat.value}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Referral Banner */}
        <Link to="/referrals" className="block group" data-testid="referral-banner">
          <div className="relative overflow-hidden rounded-xl bg-gradient-to-r from-purple-600 via-pink-500 to-orange-400 p-[1px]">
            <div className="relative bg-gradient-to-r from-purple-600/95 via-pink-500/95 to-orange-400/95 rounded-[11px] px-6 py-4 flex items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 bg-white/20 backdrop-blur rounded-full flex items-center justify-center flex-shrink-0">
                  <Gift className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-white font-bold text-base sm:text-lg">{t('dashboard.referralTitle')}</p>
                  <p className="text-white/80 text-xs sm:text-sm">{t('dashboard.referralSubtitle')}</p>
                </div>
              </div>
              <Button size="sm" className="bg-white text-purple-700 hover:bg-white/90 font-semibold shadow-lg flex-shrink-0 group-hover:scale-105 transition-transform" data-testid="referral-banner-btn">
                {t('dashboard.referralCta')} <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
              </Button>
            </div>
          </div>
        </Link>

        {/* Discord Banner */}
        <a
          href="https://discord.gg/aZ8Pp4ws"
          target="_blank"
          rel="noopener noreferrer"
          className="block group mt-4 mb-4"
          data-testid="discord-banner"
        >
          <div className="relative overflow-hidden rounded-xl bg-[#5865F2] hover:bg-[#4752C4] transition-colors shadow-lg shadow-[#5865F2]/20 hover:shadow-[#5865F2]/40">
            <div className="absolute right-0 top-0 w-40 h-full bg-white/5 -skew-x-12 translate-x-10" />
            <div className="relative px-6 py-4 flex items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 bg-white/20 backdrop-blur rounded-full flex items-center justify-center flex-shrink-0">
                  <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.947 2.418-2.157 2.418z" />
                  </svg>
                </div>
                <div>
                  <p className="text-white font-bold text-base sm:text-lg">Join our Discord Community</p>
                  <p className="text-white/80 text-xs sm:text-sm">Connect with investors, share deals & get exclusive tips</p>
                </div>
                {discordCount && (
                  <div className="hidden sm:flex items-center gap-3 ml-2" data-testid="discord-member-count">
                    <span className="flex items-center gap-1.5 bg-white/15 backdrop-blur px-2.5 py-1 rounded-full text-white text-xs font-medium">
                      <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
                      {discordCount.online} online
                    </span>
                    <span className="flex items-center gap-1.5 bg-white/15 backdrop-blur px-2.5 py-1 rounded-full text-white text-xs font-medium">
                      {discordCount.members} members
                    </span>
                  </div>
                )}
              </div>
              <Button size="sm" className="bg-white text-[#5865F2] hover:bg-white/90 font-semibold shadow-lg flex-shrink-0 group-hover:scale-105 transition-transform" data-testid="discord-banner-btn">
                Join Discord <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
              </Button>
            </div>
          </div>
        </a>

        {/* Directory Banner */}
        <Link to="/directory/register" className="block group mt-4 mb-4" data-testid="directory-banner">
          <div className="relative overflow-hidden rounded-xl bg-gradient-to-r from-teal-500 via-emerald-500 to-green-500 shadow-lg shadow-emerald-500/20 hover:shadow-emerald-500/40 transition-shadow">
            <div className="absolute right-0 top-0 w-60 h-full bg-white/5 -skew-x-12 translate-x-10" />
            <div className="absolute right-20 top-0 w-20 h-full bg-white/5 -skew-x-12 translate-x-10" />
            <div className="relative px-6 py-4 flex items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 bg-white/20 backdrop-blur rounded-full flex items-center justify-center flex-shrink-0">
                  <Building2 className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-white font-bold text-base sm:text-lg">Are you an Agent, Contractor, Lender or Wholesaler?</p>
                  <p className="text-white/80 text-xs sm:text-sm">Get listed in our Business Directory and connect with real estate investors nationwide</p>
                </div>
              </div>
              <Button size="sm" className="bg-white text-emerald-700 hover:bg-white/90 font-semibold shadow-lg flex-shrink-0 group-hover:scale-105 transition-transform" data-testid="directory-banner-btn">
                Register Now <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
              </Button>
            </div>
          </div>
        </Link>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Recent Deals */}
          <div className="lg:col-span-2">
            <Card className="border-purple-100 dark:border-purple-900/50 dark:bg-gray-800/50">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-lg dark:text-white">{t('dashboard.recentDeals')}</CardTitle>
                <Link to="/deals">
                  <Button variant="ghost" size="sm" className="text-purple-600 dark:text-pink-400 hover:text-purple-700 dark:hover:text-purple-300">
                    {t('common.viewAll')}
                  </Button>
                </Link>
              </CardHeader>
              <CardContent>
                {deals.length === 0 ? (
                  <div className="text-center py-8">
                    <Home className="w-12 h-12 text-purple-200 dark:text-purple-800 mx-auto mb-3" />
                    <p className="text-gray-500 dark:text-gray-400">{t('dashboard.noDeals')}</p>
                    <Link to="/analyzer">
                      <Button variant="link" className="text-purple-600 dark:text-pink-400 mt-2">
                        {t('dashboard.startFirst')}
                      </Button>
                    </Link>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {deals.map((deal) => (
                      <div
                        key={deal.id}
                        data-testid={`dashboard-deal-${deal.id}`}
                        className="flex flex-col sm:flex-row sm:items-center justify-between p-4 rounded-xl bg-gradient-to-r from-purple-50/50 to-pink-50/50 dark:from-purple-900/20 dark:to-pink-900/20 hover:from-purple-50 hover:to-pink-50 dark:hover:from-purple-900/30 dark:hover:to-pink-900/30 transition-colors cursor-pointer group"
                      >
                        <div className="flex items-start gap-4 mb-3 sm:mb-0">
                          <div className="w-12 h-12 bg-white dark:bg-gray-700 rounded-lg flex items-center justify-center shadow-sm border border-purple-100 dark:border-purple-800">
                            <Home className="w-6 h-6 text-purple-400" />
                          </div>
                          <div>
                            <h3 className="font-semibold text-gray-900 dark:text-white">{deal.name}</h3>
                            <p className="text-sm text-gray-500 dark:text-gray-400">{deal.city}, {deal.state}</p>
                            <div className="flex gap-2 mt-2">
                              {getStatusBadge(deal.status)}
                              {getStrategyBadge(deal.strategy)}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="text-left sm:text-right">
                            <p className="text-lg font-bold text-green-600 dark:text-green-400">
                              {formatCurrency(deal.profit)}
                            </p>
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                              {formatPercent(deal.roi)} {t('common.roi')}
                            </p>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            data-testid={`delete-deal-${deal.id}`}
                            className="opacity-0 group-hover:opacity-100 transition-opacity text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/30"
                            onClick={(e) => handleDeleteDeal(deal.id, e)}
                            disabled={deletingId === deal.id}
                          >
                            {deletingId === deal.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Trash2 className="w-4 h-4" />
                            )}
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Quick Actions & Activity */}
          <div className="space-y-6">
            {/* Product Video */}
            <Card className="border-purple-100 dark:border-purple-900/50 dark:bg-gray-800/50 overflow-hidden" data-testid="dashboard-video">
              <CardContent className="p-0">
                <video
                  autoPlay
                  muted
                  loop
                  playsInline
                  controls
                  className="w-full rounded-t-lg"
                  data-testid="dashboard-video-player"
                  ref={(el) => {
                    if (el) {
                      el.play().then(() => {
                        el.muted = false;
                      }).catch(() => {});
                    }
                  }}
                >
                  <source src="https://customer-assets.emergentagent.com/job_88b20312-eee5-4cdc-910d-99810735713c/artifacts/ibhhyw0z_PROPERTYARV%20%281%29.mp4" type="video/mp4" />
                </video>
                {/* YouTube Channel Banner */}
                <div className="bg-gradient-to-r from-red-600 to-red-700 p-4" data-testid="youtube-banner">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <svg className="w-7 h-7 text-white" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M23.5 6.19a3.02 3.02 0 0 0-2.12-2.14C19.5 3.5 12 3.5 12 3.5s-7.5 0-9.38.55A3.02 3.02 0 0 0 .5 6.19 31.6 31.6 0 0 0 0 12a31.6 31.6 0 0 0 .5 5.81 3.02 3.02 0 0 0 2.12 2.14c1.88.55 9.38.55 9.38.55s7.5 0 9.38-.55a3.02 3.02 0 0 0 2.12-2.14A31.6 31.6 0 0 0 24 12a31.6 31.6 0 0 0-.5-5.81zM9.75 15.02V8.98L15.5 12l-5.75 3.02z"/>
                      </svg>
                      <div>
                        <p className="text-white font-bold text-sm">PropertyARV</p>
                        <p className="text-red-200 text-xs">Tutorials & Walkthroughs</p>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <a
                      href="https://www.youtube.com/@PropertyARV?sub_confirmation=1"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-1 flex items-center justify-center gap-1.5 bg-white text-red-600 font-semibold text-sm py-2 rounded-full hover:bg-red-50 transition-colors"
                      data-testid="youtube-subscribe-btn"
                    >
                      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>
                      Subscribe
                    </a>
                    <a
                      href="https://www.youtube.com/@PropertyARV"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center gap-1.5 bg-red-500/30 text-white text-sm py-2 px-3 rounded-full hover:bg-red-500/50 transition-colors"
                      data-testid="youtube-like-btn"
                    >
                      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3zM7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3"/></svg>
                      Like
                    </a>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText('https://www.youtube.com/@PropertyARV');
                        toast.success('Channel link copied!');
                      }}
                      className="flex items-center justify-center gap-1.5 bg-red-500/30 text-white text-sm py-2 px-3 rounded-full hover:bg-red-500/50 transition-colors"
                      data-testid="youtube-share-btn"
                    >
                      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></svg>
                      Share
                    </button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Plan Usage Widget */}
            {usage && (
              <Card className="border-purple-100 dark:border-purple-900/50 dark:bg-gray-800/50 overflow-hidden" data-testid="usage-widget">
                <div className="h-1 bg-gradient-to-r from-purple-500 to-pink-500" />
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg dark:text-white flex items-center gap-2">
                      <Gauge className="w-5 h-5 text-purple-500" />
                      {t('dashboard.planUsage')}
                    </CardTitle>
                    <Badge className={`border-0 font-semibold text-xs ${
                      usage.plan === 'free' ? 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300' :
                      usage.plan === 'starter' ? 'bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400' :
                      usage.plan === 'pro' ? 'bg-purple-100 dark:bg-purple-900/40 text-purple-600 dark:text-purple-400' :
                      'bg-amber-100 dark:bg-amber-900/40 text-amber-600 dark:text-amber-400'
                    }`} data-testid="usage-plan-badge">
                      {usage.plan.charAt(0).toUpperCase() + usage.plan.slice(1)}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  {/* Feature Usage Rows */}
                  {[
                    { key: 'deals', label: t('dashboard.dealAnalyses'), icon: '📊' },
                    { key: 'comps', label: 'Comp Searches', icon: '🔍' },
                    { key: 'projects', label: t('dashboard.projects'), icon: '📁' },
                    { key: 'crmLeads', label: t('dashboard.crmLeads'), icon: '👥' },
                    { key: 'aiChats', label: t('dashboard.aiChats'), icon: '💬' },
                    { key: 'contractors', label: t('dashboard.contractors'), icon: '🔧' },
                    { key: 'contracts', label: 'Contracts', icon: '📝' },
                    { key: 'exports', label: t('dashboard.exports'), icon: '📤' },
                    { key: 'reports', label: 'Reports', icon: '📄' },
                  ].map(({ key, label }) => {
                    const limit = usage.limits[key];
                    const used = usage.usage[key] || 0;
                    const isUnlimited = limit === -1;
                    const pct = isUnlimited ? 0 : Math.min((used / Math.max(limit, 1)) * 100, 100);
                    const isAtLimit = !isUnlimited && used >= limit;
                    const isNearLimit = !isUnlimited && pct >= 80;
                    return (
                      <div key={key} data-testid={`${key}-usage`}>
                        <div className="flex justify-between items-baseline mb-1">
                          <span className="text-xs font-medium text-gray-600 dark:text-gray-400">{label}</span>
                          <span className={`text-xs font-bold ${isAtLimit ? 'text-red-500' : isNearLimit ? 'text-amber-500' : 'text-gray-600 dark:text-gray-400'}`}>
                            {isUnlimited ? `${used} / ∞` : `${used} / ${limit}`}
                          </span>
                        </div>
                        {!isUnlimited ? (
                          <Progress value={pct} className={`h-1.5 ${isAtLimit ? '[&>div]:bg-red-500' : isNearLimit ? '[&>div]:bg-amber-500' : '[&>div]:bg-gradient-to-r [&>div]:from-purple-500 [&>div]:to-pink-500'}`} />
                        ) : (
                          <div className="h-1.5 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center">
                            <Crown className="w-3 h-3 text-purple-500 ml-1" />
                          </div>
                        )}
                      </div>
                    );
                  })}

                  {/* Monthly Reset Note */}
                  <p className="text-xs text-gray-400 dark:text-gray-500 pt-1">{t('dashboard.resetsMonthly')}</p>

                  {/* Upgrade CTA */}
                  {usage.plan === 'free' && (
                    <Link to="/pricing">
                      <Button size="sm" className="w-full bg-gradient-to-r from-purple-600 to-pink-500 hover:from-purple-700 hover:to-pink-600 text-white" data-testid="usage-upgrade-btn">
                        {t('dashboard.upgradePlan')} <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
                      </Button>
                    </Link>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Quick Actions */}
            <Card className="border-purple-100 dark:border-purple-900/50 dark:bg-gray-800/50">
              <CardHeader>
                <CardTitle className="text-lg dark:text-white">{t('dashboard.quickActions')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <Link to="/analyzer">
                  <Button variant="outline" className="w-full justify-start h-auto py-3 border-purple-200 dark:border-purple-800 hover:bg-purple-50 dark:hover:bg-purple-900/30 hover:border-purple-300 dark:hover:border-purple-700">
                    <Calculator className="w-5 h-5 mr-3 text-purple-500" />
                    <div className="text-left">
                      <p className="font-medium dark:text-white">{t('dashboard.analyzeNewDeal')}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">{t('dashboard.calculateRoi')}</p>
                    </div>
                  </Button>
                </Link>
                <Link to="/comps">
                  <Button variant="outline" className="w-full justify-start h-auto py-3 border-purple-200 dark:border-purple-800 hover:bg-purple-50 dark:hover:bg-purple-900/30 hover:border-purple-300 dark:hover:border-purple-700">
                    <TrendingUp className="w-5 h-5 mr-3 text-pink-500" />
                    <div className="text-left">
                      <p className="font-medium dark:text-white">{t('dashboard.findComps')}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">{t('dashboard.searchComps')}</p>
                    </div>
                  </Button>
                </Link>
                <Link to="/repair-estimator">
                  <Button variant="outline" className="w-full justify-start h-auto py-3 border-purple-200 dark:border-purple-800 hover:bg-purple-50 dark:hover:bg-purple-900/30 hover:border-purple-300 dark:hover:border-purple-700">
                    <DollarSign className="w-5 h-5 mr-3 text-purple-500" />
                    <div className="text-left">
                      <p className="font-medium dark:text-white">{t('dashboard.estimateRepairs')}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">{t('dashboard.calculateRenovation')}</p>
                    </div>
                  </Button>
                </Link>
              </CardContent>
            </Card>

            {/* Recent Activity */}
            <Card className="border-purple-100 dark:border-purple-900/50 dark:bg-gray-800/50">
              <CardHeader>
                <CardTitle className="text-lg dark:text-white">{t('dashboard.recentActivity')}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {deals.length === 0 ? (
                    <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">{t('dashboard.noRecentActivity')}</p>
                  ) : (
                    deals.slice(0, 3).map((deal, index) => (
                      <div key={deal.id} className="flex items-start gap-3">
                        <div className={`w-8 h-8 ${index % 2 === 0 ? 'bg-purple-100 dark:bg-purple-900/50' : 'bg-pink-100 dark:bg-pink-900/50'} rounded-lg flex items-center justify-center flex-shrink-0`}>
                          {index === 0 ? (
                            <CheckCircle2 className={`w-4 h-4 ${index % 2 === 0 ? 'text-purple-500 dark:text-pink-400' : 'text-pink-500 dark:text-pink-400'}`} />
                          ) : index === 1 ? (
                            <FileText className={`w-4 h-4 ${index % 2 === 0 ? 'text-purple-500 dark:text-pink-400' : 'text-pink-500 dark:text-pink-400'}`} />
                          ) : (
                            <TrendingUp className={`w-4 h-4 ${index % 2 === 0 ? 'text-purple-500 dark:text-pink-400' : 'text-pink-500 dark:text-pink-400'}`} />
                          )}
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900 dark:text-white">{t('dashboard.dealAnalyzed')}</p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">{deal.name}</p>
                          <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                            {new Date(deal.updatedAt).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
